<mat-card appearance="outlined" class="stxo-card">
  <mat-card-title>
    <mat-icon class="section-icon">receipt_long</mat-icon>
    <span i18n="@@Sales Tax Exemption">Sales Tax Exemption</span>
  </mat-card-title>
  <mat-card-content>

    <!-- Loading -->
    <div *ngIf="loading" class="stxo-row">
      <mat-spinner diameter="20"></mat-spinner>
      <span i18n="@@Loading data">Loading data</span>
    </div>

    <div class="stxo-error" *ngIf="loadError">{{loadError}}</div>

    <!-- Status header -->
    <div *ngIf="!loading" class="stxo-status">
      <div>
        <strong i18n="@@Status">Status</strong>:
        <span class="stxo-status-pill"
              [class.stxo-pill-accepted]="exemption?.status === 'ACCEPTED'"
              [class.stxo-pill-pending]="exemption?.status === 'PENDING_REVIEW' || exemption?.status === 'INFO_REQUESTED'"
              [class.stxo-pill-rejected]="exemption?.status === 'REJECTED' || exemption?.status === 'REVOKED'">
          {{statusLabel()}}
        </span>
      </div>
      <div *ngIf="exemption?.reviewedBy">
        <span i18n="@@Reviewed by">Reviewed by</span>
        {{exemption.reviewedBy.userName}}
        ({{exemption.reviewedAt | date:'short'}})
      </div>
    </div>

    <!-- Read-only summary when not editing and we have an exemption -->
    <ng-container *ngIf="!loading && !editing && exemption">
      <div class="stxo-row">
        <strong i18n="@@Type">Type</strong>: {{typeLabel(exemption.type)}}
      </div>
      <div *ngIf="exemption.certificate" class="stxo-row">
        <strong i18n="@@Certificate">Certificate</strong>:
        #{{exemption.certificate.number}}
        (<span i18n="@@issued by">issued by</span> {{exemption.certificate.issuingState}})
        — {{exemption.certificate.fileName}}
        <button mat-stroked-button (click)="openCurrentCertificate()" i18n="@@View Certificate">View Certificate</button>
      </div>
      <div *ngIf="isCertificateImage() && exemption.certificateUrl" class="stxo-cert-image">
        <img i18n-alt="@@Sales tax certificate" [src]="exemption.certificateUrl" alt="Sales tax certificate" />
      </div>
      <div *ngIf="exemption.exemptStates?.length" class="stxo-row">
        <strong i18n="@@Exempt States">Exempt States</strong>:
        <span class="stxo-chips">
          <span class="stxo-chip" *ngFor="let s of exemption.exemptStates">{{s}}</span>
        </span>
      </div>

      <div *ngIf="exemption.messages?.length" class="stxo-messages">
        <h4 i18n="@@Conversation">Conversation</h4>
        <div *ngFor="let m of exemption.messages" class="stxo-message">
          <div class="stxo-message-head">
            <strong>{{m.userName}}</strong>
            <span class="stxo-mute">({{m.from}})</span>
            <span class="stxo-mute">{{m.at | date:'short'}}</span>
          </div>
          <div class="stxo-message-text">{{m.text}}</div>
        </div>
      </div>

      <div class="stxo-actions">
        <button mat-stroked-button color="primary" *ngIf="canEdit" (click)="startEdit()" i18n="@@Edit">Edit</button>
        <span *ngIf="!canEdit" class="stxo-mute" i18n="@@Currently under review">
          Currently under review. Edits will be available again after the review completes.
        </span>
      </div>
    </ng-container>

    <!-- "No exemption on file" empty state -->
    <ng-container *ngIf="!loading && !editing && !exemption">
      <p i18n="@@noExemptionOnFile">
        There is no Sales Tax Exemption on file for this organization.
        Upload a certificate and select the states you claim exemption in to
        begin a request.
      </p>
      <div class="stxo-actions">
        <button mat-flat-button color="primary" (click)="startEdit()" i18n="@@Request Exemption">Request Exemption</button>
      </div>
    </ng-container>

    <!-- Editing form -->
    <ng-container *ngIf="!loading && editing">
      <h4 i18n="@@Exemption Type">Exemption Type</h4>
      <mat-radio-group [(ngModel)]="workingType" class="stxo-radio-group">
        <mat-radio-button value="wholesale" i18n="@@wholesale">Wholesale</mat-radio-button>
        <mat-radio-button value="other" i18n="@@other">other</mat-radio-button>
        <mat-radio-button value="non-exempt" i18n="@@non-exempt">non-exempt</mat-radio-button>
      </mat-radio-group>

      <!--
        non-exempt has no certificate and no state list - register and move on.
        Show a short helper here so the user understands why the rest of the
        form (certificate fields + state matrix) is hidden in this mode.
      -->
      <div *ngIf="!requiresCertificate" class="stxo-help" i18n="@@helpNonExemptNoStates">
        Non-exempt means you do not claim a sales-tax exemption in any state. No
        certificate or state list is required. Submitting will register your
        organization with the sales-tax service so future orders can be
        attributed to you. To claim an exemption in one or more states, pick
        <strong>wholesale</strong> or <strong>other</strong> above.
      </div>

      <ng-container *ngIf="requiresCertificate">
        <h4 i18n="@@Certificate">Certificate</h4>
        <div class="stxo-form-grid">
          <mat-form-field appearance="outline">
            <mat-label i18n="@@Certificate Number">Certificate Number</mat-label>
            <input matInput [(ngModel)]="workingCertNumber" placeholder="e.g. ABC-12345"
                   i18n-placeholder="@@placeholderCertNumber" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label i18n="@@Issuing State">Issuing State</mat-label>
            <mat-select [(value)]="workingIssuingState">
              <mat-option *ngFor="let s of stateOptions" [value]="s.code">{{s.name}} ({{s.code}})</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Certificate file -->
        <div class="stxo-form-row">
          <ng-container *ngIf="hasUsableCertificate">
            <span i18n="@@Current file">Current file</span>: <strong>{{workingFileName || workingFileKey}}</strong>
            <button mat-stroked-button (click)="openCurrentCertificate()" *ngIf="exemption?.certificateUrl"
                    i18n="@@View Certificate">View Certificate</button>
          </ng-container>

          <input #certFileInput type="file" hidden [accept]="acceptedMimeTypes"
                 (change)="onFileSelected($event.target)" />
          <button mat-stroked-button type="button" (click)="certFileInput.click()">
            <mat-icon>upload_file</mat-icon>
            <span *ngIf="!hasUsableCertificate" i18n="@@Upload Certificate">Upload Certificate</span>
            <span *ngIf="hasUsableCertificate" i18n="@@Replace Certificate">Replace Certificate</span>
          </button>
        </div>

        <div class="stxo-help" i18n="@@helpCertificateFile">
          Accepted file types: PNG, JPEG, GIF, PDF. Maximum size 10&nbsp;MB.
        </div>

        <div *ngIf="uploadState === 'UPLOADING'" class="stxo-row">
          <mat-progress-bar mode="determinate" [value]="uploadProgress"></mat-progress-bar>
          <span>{{uploadProgress}}%</span>
        </div>
        <div *ngIf="uploadState === 'UPLOADED'" class="stxo-success">
          <mat-icon>check_circle</mat-icon>
          <span i18n="@@File uploaded">File uploaded</span>
        </div>
        <div *ngIf="uploadState === 'ERROR'" class="stxo-error">{{uploadError}}</div>

        <h4 i18n="@@Exempt States">Exempt States</h4>
        <div class="stxo-help" i18n="@@helpExemptStates">
          Select the states you are claiming exemption in. States the Service Provider
          recognizes as accepting your certificate as reciprocal evidence are
          pre-checked. Selecting non-reciprocal states will trigger a warning —
          you may still claim exemption there but you must provide an additional
          certificate from each such state out-of-band.
        </div>
        <app-sales-tax-states-matrix
          [config]="config"
          [type]="workingType"
          [issuingState]="workingIssuingState"
          [(selectedStates)]="workingExemptStates">
        </app-sales-tax-states-matrix>
      </ng-container>

      <mat-form-field appearance="outline" style="width: 100%; margin-top: 12px;">
        <mat-label i18n="@@Note to Reviewer">Note to Reviewer</mat-label>
        <textarea matInput rows="3" [(ngModel)]="workingMessage"></textarea>
      </mat-form-field>

      <div class="stxo-error" *ngIf="saveError">{{saveError}}</div>

      <div class="stxo-actions">
        <button mat-flat-button color="primary"
                [disabled]="!readyToSave"
                (click)="save()">
          <span i18n="@@Submit Request">Submit Request</span>
        </button>
        <button mat-stroked-button [disabled]="saving" (click)="cancelEdit()" i18n="dialog dismissal|Button that closes a dialog and discards any unsaved edits. It never removes or destroys existing data - the Delete button does that. Must translate to a word meaning 'abandon this action', not 'erase'.@@Cancel">Cancel</button>
        <mat-spinner *ngIf="saving" diameter="18"></mat-spinner>
      </div>
    </ng-container>

    <div *ngIf="saveMessage && !editing" class="stxo-success">
      <mat-icon>check_circle</mat-icon>
      <span>{{saveMessage}}</span>
    </div>

    <!--
      Non-blocking warnings from the most recent save. The save itself
      succeeded (DB state is authoritative); these surface downstream
      issues such as a sales-tax service sync failure so the org is aware
      a retry from the service provider may be needed.
    -->
    <div *ngIf="saveWarnings && saveWarnings.length > 0 && !editing" class="stxo-warning-list">
      <div *ngFor="let w of saveWarnings" class="stxo-warning">
        <mat-icon>warning</mat-icon>
        <span>{{w.message}}</span>
      </div>
    </div>

  </mat-card-content>
</mat-card>


